import React, { useEffect, useState } from 'react';
import {
    ArrowLeft,
    ArrowRight,
    Bell,
    CalendarCheck2,
    CalendarDays,
    Check,
    CheckCircle2,
    Download,
    GitCompareArrows,
    MessageSquareText,
    Pause,
    RefreshCw,
    SlidersHorizontal,
    Smartphone,
    Sparkles,
    Table2,
    ToggleRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from './Modal';
import { Col, Row } from '../../lib/utils';
import { Body2Regular, Body3Regular, CaptionRegular, Head4 } from '../../lib/font';
import { CustomCheckbox } from '../Grid/Grid';

type Props = {
    onClose: () => void;
    onCloseNeverSee: () => void;
};

/**
 * ═══════════════════════════════════════════════════════════
 * 🔴 「다시 보지 않기」 쿠키 키 — **릴리스마다 바꾼다**
 * ═══════════════════════════════════════════════════════════
 * 키를 그대로 두면 이전 릴리스에서 체크한 원장님에게는 새 슬라이드가
 * **영영 안 보인다.** 키를 바꾸면 캐러셀이 전 센터에 다시 뜬다 —
 * 그게 새 기능을 알리는 유일한 경로다.
 *
 * ⚠️ **옛 키를 읽어 넘겨받는 마이그레이션을 넣지 마라.** 넣으면 다시 안 뜨고,
 *    키를 바꾼 목적 자체가 없어진다.
 *
 * ⚠️ 슬라이드를 추가·교체하면 **여기도 같이 바꾼다.** 그래서 `UPDATE_SLIDES`
 *    바로 옆에 둔다 — `App.tsx` 에 떨어져 있던 동안 실제로 한 번 잊었다.
 *
 * 관례: `updatePopup_{릴리스}_dismissed`
 *   updatePopup_gifticon2026_dismissed  (2026-08 · 기프티콘 선물)
 *   updatePopup_signal202609_dismissed  (2026-09 · 손실 시그널)
 *   updatePopup_session202610_dismissed (2026-10 · 세션 현황)  ← 지금
 */
export const UPDATE_POPUP_DISMISS_KEY = 'updatePopup_session202610_dismissed';

/**
 * ═══════════════════════════════════════════════════════════
 * 「이번 로그인에는 이미 봤다」 — 세션 쿠키
 * ═══════════════════════════════════════════════════════════
 * 위의 `..._dismissed` 는 **체크박스를 누른 사람**만 남기는 영구 기록이다.
 * 그냥 닫은 사람은 아무것도 남기지 않아서, 팝업을 다시 띄울 계기가 생기면
 * 그때마다 또 떴다 — 대표 제보(2026-09-04) 「화면 옮길 때마다 뜬다」.
 *
 * 🔴 App.tsx 의 `prevAuthRef` 는 **메모리 변수**다. 새로고침 한 번이면
 *    비어서 팝업이 다시 뜬다. 그리고 이 웹에는 스스로 새로고침하는 자리가
 *    여럿 있다 — 정산 비밀번호 화면의 취소(`window.location.href = '/'`),
 *    저장값 만료(`utils.ts` 의 `window.location.replace('/')`),
 *    센터 열람 전환 등. 라우팅이 아니라 **문서가 다시 뜨는** 자리들이다.
 *
 * 그래서 닫았다는 사실을 브라우저에 남긴다.
 *   · **만료 시각을 주지 않는다** → 브라우저를 닫으면 사라지는 세션 쿠키
 *   · localStorage 가 아니라 쿠키인 이유는 위와 같다 —
 *     `clearLocalStorage()` 가 통째로 비운다
 *   · 탭이 여러 개여도 쿠키는 공유된다. sessionStorage 였다면
 *     새 탭마다 다시 떴다
 *
 * 지우는 자리는 **로그인 화면에 들어설 때** 하나뿐이다(App.tsx).
 * 그래야 「다음 로그인 때 한 번 더」가 성립한다.
 */
export const UPDATE_POPUP_SEEN_KEY = 'updatePopup_session202610_seen';

/** 헤더 배지 — 이번 릴리스의 모든 슬라이드에 공통으로 표시한다. */
export const UPDATE_BADGE_LABEL = '2026. 10 UPDATE';

type SlideVisual =
    | 'sessionFlow'
    | 'supportReport'
    | 'payrollTable'
    | 'pause'
    | 'noshowNotice'
    | 'groupRules'
    | 'app';

type UpdateSlide = {
    icon: React.ElementType;
    category: string;
    title: string;
    description: string;
    menu: string;
    accent: string;
    softColor: string;
    gradient: string;
    visual: SlideVisual;
    /**
     * 「지금 보러가기」 버튼이 보낼 곳. 없으면 메뉴 위치만 안내한다.
     * 회원·수업을 먼저 골라야 하는 기능과 앱 업데이트는 바로가기 없이 안내한다.
     * route에는 실제 화면에서 사용하는 탭 쿼리까지 포함한다.
     */
    route?: string;
    bullets: Array<{
        title: string;
        description: string;
    }>;
};

/*
 * 테스트에서 읽으려고 export 합니다 (`navConfig` 과 같은 이유).
 * 문구에 없는 기능이 섞이는 것은 화면을 띄우지 않고도 막을 수 있어야 합니다.
 */
export const UPDATE_SLIDES: UpdateSlide[] = [
    {
        icon: GitCompareArrows,
        category: '세션 현황',
        title: '강사별 남은 회차를\n한 줄 흐름으로 확인해요',
        description: '지난달 남은 회차부터 이번 달 남은 회차까지 계산 과정이 그대로 보여요.',
        menu: '매출 정산 · 세션 현황',
        accent: '#3D73DD',
        softColor: '#ECF1FC',
        gradient: 'linear-gradient(145deg, #EAF2FF 0%, #F6F9FF 58%, #E7F8FF 100%)',
        visual: 'sessionFlow',
        route: '/settlement?tab=sessionReport',
        bullets: [
            { title: '지난달 남은 → 새로 판 → 수업 → 남은 회차', description: '강사별 회차와 센터 합계를 한눈에 확인해요' },
            { title: '숫자를 누르면 근거까지 보여요', description: '근거가 되는 회원과 수강권을 바로 확인해요' },
            { title: '쓰시던 엑셀 양식 그대로 내려받아요', description: '「엑셀 칸 모두」에서 내려받을 수 있어요' },
        ],
    },
    {
        icon: MessageSquareText,
        category: '라포에 알리기',
        title: '안 맞는 회차는\n라포에 바로 알려 주세요',
        description: '기록과 수강권 회차가 다르면 자동으로 찾아 드려요.',
        menu: '매출 정산 · 세션 현황 · 확인할 일',
        accent: '#C5672D',
        softColor: '#FFF0E5',
        gradient: 'linear-gradient(145deg, #FFF0E5 0%, #FFFAF6 58%, #FFF5DE 100%)',
        visual: 'supportReport',
        route: '/settlement?tab=sessionReport',
        bullets: [
            { title: '안 맞는 회원을 한곳에 모아 드려요', description: '기록과 다른 회차를 「확인할 일」에서 확인해요' },
            { title: '「라포에 알리기」 한 번이면 돼요', description: '메모와 함께 접수돼요' },
            { title: '확인이 끝나면 알림센터로 알려 드려요', description: '관리자 앱을 쓰면 푸시로도 알려 드려요' },
        ],
    },
    {
        icon: Table2,
        category: '전체 강사 급여',
        title: '강사 전체 급여를\n한 표로 확인해요',
        description: '강사를 하나씩 누르지 않아도 이번 달 급여가 한눈에 보여요.',
        menu: '매출 정산 · 급여 계산',
        accent: '#7C3AED',
        softColor: '#F3ECFF',
        gradient: 'linear-gradient(145deg, #F4EDFF 0%, #FBF9FF 55%, #EFF4FF 100%)',
        visual: 'payrollTable',
        route: '/settlement?tab=salaryCalculation',
        bullets: [
            { title: '강사별 급여와 합계를 한 표로 봐요', description: '매출·수업료·인센티브·공제·최종 급여까지 보여요' },
            { title: '표 그대로 엑셀로 내려받아요', description: '전체 강사 급여를 한 번에 내려받을 수 있어요' },
            { title: '비어 있는 수업료 구간을 알려 드려요', description: '구간이 비어 수업료가 0원이면 바로 알려 드려요' },
        ],
    },
    {
        icon: Pause,
        category: '수강권 정지',
        title: '수강권을 잠시\n멈출 수 있어요',
        description: '여행·부상으로 쉬는 회원의 수강권을 기간을 정해 멈춰 두세요.',
        menu: '회원 · 수강권 관리',
        accent: '#0F766E',
        softColor: '#E6F7F4',
        gradient: 'linear-gradient(145deg, #E7F8F5 0%, #F6FCFB 58%, #EAF6FF 100%)',
        visual: 'pause',
        bullets: [
            { title: '시작일과 해제일을 정해요', description: '해제일 없이 멈춰 둘 수도 있어요' },
            { title: '멈춘 동안은 앱 예약을 쉬어요', description: '회원이 앱에서 예약을 신청할 수 없어요' },
            { title: '쉬었던 만큼 만료일이 늘어나요', description: '정지를 해제하면 쉰 기간이 반영돼요' },
        ],
    },
    {
        icon: Bell,
        category: '노쇼 알림 문구',
        title: '노쇼 처리할 때\n알림 문구를 고를 수 있어요',
        description: '회원에게 가는 알림을 상황에 맞게 보내세요.',
        menu: '스케줄 · 수업 상세',
        accent: '#BE4B75',
        softColor: '#FCECF2',
        gradient: 'linear-gradient(145deg, #FCECF2 0%, #FFF8FA 58%, #F5EEFF 100%)',
        visual: 'noshowNotice',
        bullets: [
            { title: '「노쇼」와 「당일 취소」 중 골라요', description: '상황에 맞는 알림 문구를 선택해요' },
            { title: '보내기 전에 문구를 미리 봐요', description: '회원이 받을 알림 내용을 확인할 수 있어요' },
            { title: '회차 차감과 정산은 지금과 같아요', description: '문구를 바꿔도 노쇼로 처리돼요' },
        ],
    },
    {
        icon: SlidersHorizontal,
        category: '그룹 수업 예약 규칙',
        title: '그룹 수업마다\n예약 규칙을 따로 정해요',
        description: '센터 기본값 대신 이 수업만의 규칙을 쓸 수 있어요.',
        menu: '그룹 수업 관리',
        accent: '#278058',
        softColor: '#E8F6EC',
        gradient: 'linear-gradient(145deg, #EAF6EB 0%, #F8FCF7 58%, #EAF7F3 100%)',
        visual: 'groupRules',
        bullets: [
            { title: '이 수업에 맞는 예약 규칙을 정해요', description: '예약 오픈·늦은 취소·자동 폐강·대기 자동 확정까지요' },
            { title: '앞으로 열릴 회차에도 한 번에 반영해요', description: '바꾼 규칙을 예정된 회차에 함께 적용해요' },
            { title: '회차 하나만 따로 바꿀 수도 있어요', description: '원하는 회차의 예약 규칙만 바꿔요' },
        ],
    },
    {
        icon: Smartphone,
        category: '꼭 확인해 주세요',
        title: '라포 앱도 최신 버전으로\n업데이트 부탁드려요',
        description: '관리자웹과 앱의 새로운 기능을 안정적으로 사용하려면 회원·강사 앱을 최신 상태로 유지해 주세요.',
        menu: '회원 앱 · 강사 앱',
        accent: '#2563EB',
        softColor: '#EAF2FF',
        gradient: 'linear-gradient(145deg, #E9F2FF 0%, #F7FAFF 52%, #EAFBFF 100%)',
        visual: 'app',
        bullets: [
            { title: '회원과 강사님께 업데이트 안내', description: '사용 중인 라포 앱을 스토어에서 최신 버전으로 업데이트' },
            { title: '자동 업데이트 사용 권장', description: '앞으로 추가될 기능과 안정성 개선을 놓치지 않도록 설정' },
            { title: '앱 연동 상태도 함께 확인', description: '관리자웹 스케줄의 앱 연동 배지로 연결 상태를 점검' },
        ],
    },
];

const MiniWindow = ({
    children,
    title,
    accent,
}: {
    children: React.ReactNode;
    title: string;
    accent: string;
}) => (
    <div
        style={{
            width: '100%',
            padding: 14,
            border: '1px solid rgba(255,255,255,0.72)',
            borderRadius: 16,
            backgroundColor: 'rgba(255,255,255,0.82)',
            boxShadow: '0 14px 34px rgba(16, 24, 40, 0.11)',
            backdropFilter: 'blur(8px)',
        }}
    >
        <Row style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 13 }}>
            <CaptionRegular style={{ display: 'block', color: '#344054', fontWeight: 700 }}>{title}</CaptionRegular>
            <Row style={{ gap: 4 }}>
                {[0, 1, 2].map((dot) => (
                    <span
                        key={dot}
                        style={{
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            backgroundColor: dot === 0 ? accent : '#D0D5DD',
                        }}
                    />
                ))}
            </Row>
        </Row>
        {children}
    </div>
);

/** 실제 화면의 핵심만 남긴 미리보기. 숫자와 이름은 모두 설명용 예시다. */
const FeatureVisual = ({ type, accent }: { type: SlideVisual; accent: string }) => {
    if (type === 'sessionFlow') {
        return (
            <MiniWindow title="강사별 회차 흐름" accent={accent}>
                <Row style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                    <span style={{ color: '#667085', fontSize: 12 }}>2026년 10월</span>
                    <Row style={{ alignItems: 'center', gap: 5, color: accent, fontSize: 12, fontWeight: 700 }}>
                        <Download size={13} /> 엑셀 칸 모두
                    </Row>
                </Row>
                <div style={{ display: 'grid', gridTemplateColumns: '44px repeat(4, minmax(0, 1fr))', gap: 5 }}>
                    {[
                        ['강사', '김○○'],
                        ['지난달 남은', '120'],
                        ['새로 판', '+40'],
                        ['수업', '−35'],
                        ['남은 회차', '125'],
                    ].map(([label, value], index) => (
                        <Col
                            key={label}
                            style={{
                                position: 'relative',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                                padding: '12px 2px',
                                borderRadius: 9,
                                border: `1px solid ${index === 4 ? accent : '#E4E7EC'}`,
                                backgroundColor: index === 4 ? accent : '#FFFFFF',
                            }}
                        >
                            <span
                                style={{
                                    maxWidth: 40,
                                    minHeight: 32,
                                    color: index === 4 ? '#FFFFFF' : '#667085',
                                    fontSize: 12,
                                    lineHeight: '16px',
                                    textAlign: 'center',
                                    wordBreak: 'keep-all',
                                }}
                            >
                                {label}
                            </span>
                            <span
                                style={{
                                    color: index === 4 ? '#FFFFFF' : index === 2 ? accent : '#344054',
                                    fontSize: index === 0 ? 13 : 19,
                                    fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                    letterSpacing: -0.5,
                                }}
                            >
                                {value}
                            </span>
                            {index > 1 && <ArrowRight size={9} color="#98A2B3" style={{ position: 'absolute', left: -8, top: 47, backgroundColor: '#F8FAFF' }} />}
                        </Col>
                    ))}
                </div>
                <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '14px 4px', borderBottom: '1px solid #EAECF0' }}>
                    <span style={{ color: '#475467', fontSize: 13, fontWeight: 700 }}>센터 합계</span>
                    <span style={{ color: accent, fontSize: 19, fontWeight: 700 }}>125회</span>
                </Row>
                <Row style={{ alignItems: 'center', gap: 8, marginTop: 12, padding: '9px 10px', borderRadius: 10, backgroundColor: `${accent}0D` }}>
                    <Table2 size={17} color={accent} style={{ flexShrink: 0 }} />
                    <span style={{ color: '#344054', fontSize: 13, fontWeight: 700 }}>숫자에서 회원·수강권까지</span>
                </Row>
            </MiniWindow>
        );
    }

    if (type === 'supportReport') {
        return (
            <Col style={{ width: '100%', gap: 16 }}>
                <MiniWindow title="확인할 일" accent={accent}>
                    <Col style={{ gap: 14, padding: 14, border: '1px solid #F1E4D9', borderRadius: 12, backgroundColor: '#FFFFFF' }}>
                        <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                            <span style={{ color: '#344054', fontSize: 16, fontWeight: 700 }}>김○○ 회원</span>
                            <span style={{ padding: '4px 8px', borderRadius: 6, color: '#A84B1E', backgroundColor: '#FFF0E5', fontSize: 12, fontWeight: 700 }}>확인 필요</span>
                        </Row>
                        <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '12px 10px', borderRadius: 9, backgroundColor: '#F9FAFB' }}>
                            <Col style={{ gap: 4 }}>
                                <span style={{ color: '#667085', fontSize: 12 }}>수업 기록</span>
                                <span style={{ color: '#344054', fontSize: 20, fontWeight: 700 }}>12회</span>
                            </Col>
                            <span style={{ color: accent, fontSize: 22 }}>≠</span>
                            <Col style={{ gap: 4, textAlign: 'right' }}>
                                <span style={{ color: '#667085', fontSize: 12 }}>수강권 회차</span>
                                <span style={{ color: accent, fontSize: 20, fontWeight: 700 }}>10회</span>
                            </Col>
                        </Row>
                        <span style={{ padding: '9px 10px', border: '1px solid #EAECF0', borderRadius: 7, color: '#667085', fontSize: 12 }}>메모 · 남은 회차를 확인해 주세요.</span>
                        <Row
                            style={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 7,
                                padding: '10px 8px',
                                borderRadius: 9,
                                color: '#FFFFFF',
                                backgroundColor: accent,
                                fontSize: 14,
                                fontWeight: 700,
                            }}
                        >
                            <MessageSquareText size={16} /> 라포에 알리기
                        </Row>
                    </Col>
                </MiniWindow>
                <Row
                    style={{
                        alignItems: 'center',
                        gap: 11,
                        padding: '14px 16px',
                        borderRadius: 13,
                        border: `1px solid ${accent}25`,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 8px 18px rgba(101, 60, 30, 0.06)',
                    }}
                >
                    <Bell size={21} color={accent} style={{ flexShrink: 0 }} />
                    <Col style={{ gap: 4 }}>
                        <span style={{ color: '#667085', fontSize: 12 }}>알림센터</span>
                        <span style={{ color: '#344054', fontSize: 14, fontWeight: 700 }}>라포가 확인을 마쳤어요</span>
                    </Col>
                    <CheckCircle2 size={17} color={accent} style={{ marginLeft: 'auto', flexShrink: 0 }} />
                </Row>
            </Col>
        );
    }

    if (type === 'payrollTable') {
        return (
            <MiniWindow title="급여 계산 · 전체 강사" accent={accent}>
                <Row style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <span style={{ color: '#667085', fontSize: 12 }}>2026년 10월</span>
                    <Row style={{ alignItems: 'center', gap: 5, color: accent, fontSize: 12, fontWeight: 700 }}>
                        <Download size={13} /> 엑셀 내려받기
                    </Row>
                </Row>
                <div style={{ border: '1px solid #EAE4F4', borderRadius: 11, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                        <thead style={{ color: '#667085', backgroundColor: '#F7F4FC', fontSize: 12 }}>
                            <tr>
                                <th style={{ padding: '11px 9px', textAlign: 'left', fontWeight: 500 }}>강사</th>
                                <th style={{ padding: '11px 5px', textAlign: 'right', fontWeight: 500 }}>수업료</th>
                                <th style={{ padding: '11px 9px', textAlign: 'right', fontWeight: 700, color: accent }}>최종 급여</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[
                                ['김○○', '1,800,000', '2,100,000'],
                                ['이○○', '1,500,000', '1,700,000'],
                                ['박○○', '1,200,000', '1,400,000'],
                            ].map(([name, fee, salary]) => (
                                <tr key={name} style={{ color: '#475467', borderTop: '1px solid #F0ECF5', backgroundColor: '#FFFFFF' }}>
                                    <td style={{ padding: '14px 9px', fontWeight: 700 }}>{name}</td>
                                    <td style={{ padding: '14px 5px', textAlign: 'right' }}>{fee}</td>
                                    <td style={{ padding: '14px 9px', textAlign: 'right', fontWeight: 700, color: '#344054' }}>{salary}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot style={{ color: accent, backgroundColor: `${accent}0C`, fontWeight: 700, borderTop: '1px solid #EAE4F4' }}>
                            <tr>
                                <td style={{ padding: '13px 9px' }}>합계</td>
                                <td style={{ padding: '13px 5px', textAlign: 'right' }}>4,500,000</td>
                                <td style={{ padding: '13px 9px', textAlign: 'right' }}>5,200,000</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <span style={{ display: 'block', marginTop: 12, color: '#667085', fontSize: 12, textAlign: 'right' }}>단위: 원</span>
            </MiniWindow>
        );
    }

    if (type === 'pause') {
        return (
            <MiniWindow title="수강권 관리" accent={accent}>
                <Col style={{ padding: '18px 16px', gap: 17, borderRadius: 13, border: `1px solid ${accent}25`, backgroundColor: '#FFFFFF' }}>
                    <Row style={{ alignItems: 'center', gap: 11 }}>
                        <Row style={{ width: 42, height: 42, alignItems: 'center', justifyContent: 'center', borderRadius: 12, backgroundColor: `${accent}12`, flexShrink: 0 }}>
                            <Pause size={23} color={accent} />
                        </Row>
                        <Col style={{ gap: 4 }}>
                            <span style={{ color: '#667085', fontSize: 12 }}>김○○ 회원</span>
                            <span style={{ color: '#344054', fontSize: 16, fontWeight: 700 }}>개인 수강권</span>
                        </Col>
                    </Row>
                    <Col style={{ gap: 7, padding: '13px 12px', borderRadius: 10, color: accent, backgroundColor: `${accent}10` }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>정지 중</span>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>10/1 ~ 10/31 해제 예정</span>
                    </Col>
                    <Row style={{ alignItems: 'center', justifyContent: 'space-between', padding: '0 2px' }}>
                        <span style={{ color: '#667085', fontSize: 13 }}>남은 회차</span>
                        <span style={{ color: '#344054', fontSize: 22, fontWeight: 700 }}>12회</span>
                    </Row>
                    <Row style={{ alignItems: 'center', gap: 7, paddingTop: 14, borderTop: '1px solid #EAECF0', color: '#667085', fontSize: 12 }}>
                        <CalendarDays size={16} color={accent} style={{ flexShrink: 0 }} /> 해제하면 쉰 만큼 만료일이 늘어나요
                    </Row>
                </Col>
            </MiniWindow>
        );
    }

    if (type === 'noshowNotice') {
        return (
            <Col style={{ width: '100%', alignItems: 'center', gap: 16 }}>
                <MiniWindow title="회원에게 보낼 알림 문구" accent={accent}>
                    <Row style={{ gap: 8 }}>
                        {['노쇼', '당일 취소'].map((label, index) => (
                            <Row
                                key={label}
                                style={{
                                    flex: 1,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 7,
                                    padding: '12px 8px',
                                    border: `1px solid ${index === 1 ? accent : '#E4E7EC'}`,
                                    borderRadius: 10,
                                    color: index === 1 ? accent : '#667085',
                                    backgroundColor: index === 1 ? `${accent}0D` : '#FFFFFF',
                                    fontSize: 14,
                                    fontWeight: 700,
                                }}
                            >
                                <span
                                    style={{
                                        width: 15,
                                        height: 15,
                                        borderRadius: '50%',
                                        border: `${index === 1 ? 5 : 1}px solid ${index === 1 ? accent : '#D0D5DD'}`,
                                        backgroundColor: '#FFFFFF',
                                    }}
                                />
                                {label}
                            </Row>
                        ))}
                    </Row>
                </MiniWindow>
                <div
                    style={{
                        width: 232,
                        padding: '10px 12px 20px',
                        border: '5px solid #344054',
                        borderRadius: 26,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 12px 24px rgba(73, 37, 54, 0.10)',
                    }}
                >
                    <div style={{ width: 42, height: 4, margin: '0 auto 17px', borderRadius: 999, backgroundColor: '#344054' }} />
                    <span style={{ display: 'block', marginBottom: 11, color: '#667085', fontSize: 12, textAlign: 'center' }}>회원 알림 미리보기</span>
                    <Col style={{ gap: 10, padding: 13, borderRadius: 13, backgroundColor: `${accent}0D` }}>
                        <Row style={{ alignItems: 'center', gap: 6, color: accent, fontSize: 12, fontWeight: 700 }}>
                            <Bell size={14} /> 라포
                        </Row>
                        <span style={{ color: '#344054', fontSize: 15, fontWeight: 700 }}>수업이 당일 취소 처리되었어요!</span>
                        <span style={{ color: '#667085', fontSize: 13, lineHeight: 1.6 }}>10월8일 목요일 수업이<br />당일 취소로 처리되었어요.</span>
                    </Col>
                </div>
            </Col>
        );
    }

    if (type === 'groupRules') {
        return (
            <MiniWindow title="그룹 수업 · 예약 규칙" accent={accent}>
                <Row
                    style={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '12px 10px',
                        marginBottom: 13,
                        borderRadius: 10,
                        backgroundColor: `${accent}0D`,
                    }}
                >
                    <span style={{ color: '#344054', fontSize: 14, fontWeight: 700 }}>이 수업만 따로 정하기</span>
                    <ToggleRight size={34} color={accent} style={{ flexShrink: 0 }} />
                </Row>
                <Col style={{ padding: '0 12px', border: '1px solid #E4EAE5', borderRadius: 11, backgroundColor: '#FFFFFF' }}>
                    {[
                        ['예약 오픈', '7일 전'],
                        ['늦은 취소', '수업 3시간 전부터'],
                        ['대기 자동 확정', '사용'],
                    ].map(([label, value], index) => (
                        <Row
                            key={label}
                            style={{
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '16px 0',
                                borderTop: index === 0 ? 'none' : '1px solid #EAECF0',
                                gap: 8,
                            }}
                        >
                            <span style={{ color: '#667085', fontSize: 13 }}>{label}</span>
                            <span style={{ color: '#344054', fontSize: 13, fontWeight: 700 }}>{value}</span>
                        </Row>
                    ))}
                </Col>
                <Row style={{ alignItems: 'center', gap: 7, marginTop: 16, padding: '0 2px', color: accent, fontSize: 13, fontWeight: 700 }}>
                    <CalendarCheck2 size={17} style={{ flexShrink: 0 }} /> 앞으로 열릴 회차에도 반영해요
                </Row>
            </MiniWindow>
        );
    }

    return (
        <div
            style={{
                width: 224,
                padding: '13px 11px 17px',
                border: '7px solid #172B4D',
                borderRadius: 31,
                backgroundColor: '#FFFFFF',
                boxShadow: '0 22px 44px rgba(37, 99, 235, 0.2)',
            }}
        >
            <div style={{ width: 54, height: 5, margin: '0 auto 21px', borderRadius: 999, backgroundColor: '#172B4D' }} />
            <Col style={{ alignItems: 'center', padding: '16px 10px 19px' }}>
                <div
                    style={{
                        width: 72,
                        height: 72,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 15,
                        borderRadius: 23,
                        background: 'linear-gradient(145deg, #2563EB 0%, #60A5FA 100%)',
                    }}
                >
                    <RefreshCw size={34} color="#FFFFFF" strokeWidth={2.2} />
                </div>
                <Body2Regular
                    style={{ display: 'block', color: '#1D2939', fontWeight: 700, textAlign: 'center', marginBottom: 7 }}
                >
                    새로운 버전이 있어요
                </Body2Regular>
                <CaptionRegular style={{ color: '#667085', textAlign: 'center', lineHeight: 1.5, marginBottom: 17 }}>
                    더 안정적인 앱 사용을 위해
                    <br />
                    최신 버전으로 업데이트해 주세요.
                </CaptionRegular>
                <div
                    style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: 9,
                        color: '#FFFFFF',
                        backgroundColor: accent,
                        textAlign: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                    }}
                >
                    최신 버전으로 업데이트
                </div>
            </Col>
        </div>
    );
};

const ModalUpdatePopupPublicMember = ({ onClose, onCloseNeverSee }: Props) => {
    const [checked, setChecked] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const navigate = useNavigate();
    const slide = UPDATE_SLIDES[currentIndex];
    const Icon = slide.icon;
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === UPDATE_SLIDES.length - 1;

    const handleClose = () => {
        if (checked) {
            onCloseNeverSee();
            return;
        }
        onClose();
    };

    /**
     * 「지금 보러가기」 — 그 기능 화면으로 보내고 팝업을 닫는다.
     *
     * 🔴 **닫기를 먼저 한다.** 이동만 하고 팝업을 열어 두면 원장님이 도착한
     *    화면을 팝업이 가린다. 그리고 닫기는 `handleClose` 를 그대로 탄다 —
     *    「다시 보지 않기」 체크와 세션 기록이 여기서만 빠지면 안 된다.
     */
    const handleGo = () => {
        if (!slide.route) return;
        handleClose();
        navigate(slide.route);
    };

    const goPrevious = () => setCurrentIndex((index) => Math.max(index - 1, 0));
    const goNext = () => {
        if (isLast) {
            handleClose();
            return;
        }
        setCurrentIndex((index) => Math.min(index + 1, UPDATE_SLIDES.length - 1));
    };

    useEffect(() => {
        const handleArrowKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                setCurrentIndex((index) => Math.max(index - 1, 0));
            }
            if (event.key === 'ArrowRight') {
                setCurrentIndex((index) => Math.min(index + 1, UPDATE_SLIDES.length - 1));
            }
        };

        document.addEventListener('keydown', handleArrowKey);
        return () => document.removeEventListener('keydown', handleArrowKey);
    }, []);

    return (
        <Modal
            whiteViewWidth={920}
            whiteViewHeight={720}
            isCloseButton
            onClose={handleClose}
            isCloseOutside={false}
            borderRadius={24}
            backgroundOpacity={0.48}
        >
            <Col style={{ minHeight: '100%', padding: '28px 34px 24px', borderRadius: 24, backgroundColor: '#FFFFFF' }}>
                <Row style={{ alignItems: 'center', justifyContent: 'space-between', marginBottom: 17, paddingRight: 52 }}>
                    <Row style={{ alignItems: 'center', gap: 8 }}>
                        <Row
                            style={{
                                alignItems: 'center',
                                gap: 6,
                                padding: '6px 11px',
                                borderRadius: 999,
                                color: '#2563EB',
                                backgroundColor: '#EEF4FF',
                            }}
                        >
                            <Sparkles size={14} strokeWidth={2.4} />
                            <CaptionRegular style={{ color: 'inherit', fontWeight: 700 }}>{UPDATE_BADGE_LABEL}</CaptionRegular>
                        </Row>
                        <Body3Regular style={{ color: '#475467', fontWeight: 700 }}>Rappo 업데이트 소식</Body3Regular>
                    </Row>
                    <CaptionRegular style={{ color: '#98A2B3', fontWeight: 700 }}>
                        {String(currentIndex + 1).padStart(2, '0')} / {String(UPDATE_SLIDES.length).padStart(2, '0')}
                    </CaptionRegular>
                </Row>

                <div
                    key={slide.category}
                    style={{
                        position: 'relative',
                        minHeight: 548,
                        padding: '38px 38px 32px',
                        overflow: 'hidden',
                        border: '1px solid rgba(255,255,255,0.7)',
                        borderRadius: 22,
                        background: slide.gradient,
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: -110,
                            right: -80,
                            width: 320,
                            height: 320,
                            borderRadius: '50%',
                            backgroundColor: `${slide.accent}10`,
                        }}
                    />
                    <Row style={{ position: 'relative', minHeight: 474, alignItems: 'center', justifyContent: 'space-between', gap: 34 }}>
                        <Col style={{ width: 364, alignSelf: 'stretch', justifyContent: 'center' }}>
                            <Row
                                style={{
                                    width: 'fit-content',
                                    alignItems: 'center',
                                    gap: 7,
                                    marginBottom: 18,
                                    padding: '7px 11px',
                                    borderRadius: 9,
                                    color: slide.accent,
                                    backgroundColor: slide.softColor,
                                }}
                            >
                                <Icon size={16} color="currentColor" strokeWidth={2.3} />
                                <CaptionRegular style={{ color: 'inherit', fontWeight: 700 }}>{slide.category}</CaptionRegular>
                            </Row>
                            <Head4
                                style={{
                                    display: 'block',
                                    marginBottom: 14,
                                    color: '#101828',
                                    fontSize: 29,
                                    lineHeight: 1.35,
                                    whiteSpace: 'pre-line',
                                    letterSpacing: -0.5,
                                }}
                            >
                                {slide.title}
                            </Head4>
                            <Body3Regular
                                style={{
                                    display: 'block',
                                    marginBottom: 25,
                                    color: '#667085',
                                    lineHeight: 1.65,
                                    wordBreak: 'keep-all',
                                }}
                            >
                                {slide.description}
                            </Body3Regular>

                            <Col style={{ gap: 14 }}>
                                {slide.bullets.map((bullet) => (
                                    <Row key={bullet.title} style={{ alignItems: 'flex-start', gap: 10 }}>
                                        <div
                                            style={{
                                                width: 20,
                                                height: 20,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                marginTop: 1,
                                                borderRadius: '50%',
                                                color: '#FFFFFF',
                                                backgroundColor: slide.accent,
                                            }}
                                        >
                                            <Check size={12} strokeWidth={3} />
                                        </div>
                                        <Col style={{ gap: 3 }}>
                                            <Body3Regular style={{ color: '#344054', fontWeight: 700 }}>
                                                {bullet.title}
                                            </Body3Regular>
                                            <CaptionRegular style={{ color: '#667085', lineHeight: 1.45 }}>
                                                {bullet.description}
                                            </CaptionRegular>
                                        </Col>
                                    </Row>
                                ))}
                            </Col>

                            {/*
                              ─────────────────────────────────────────────────
                              「어디에 있다」에서 「거기로 간다」로 (2026-09-04 대표 요청)
                              ─────────────────────────────────────────────────
                              글로만 자리를 알려 주니 원장님이 상단 메뉴를 뒤져서
                              직접 찾아가야 했다. 갈 곳이 분명하면 눌러서 바로 간다.

                              🔴 **버튼을 아래에 새로 달지 않고 이 줄을 버튼으로 바꿨다.**
                                 이 카드는 `minHeight: 548` 이고 모달은 720 고정
                                 (`isWhiteOverFlowScroll` 기본 true)이라 세로 여유가
                                 10px 남짓이다. 줄을 하나 더 얹으면 긴 슬라이드에서
                                 모달 안에 스크롤바가 생긴다 — 「확인했어요」 버튼이
                                 화면 밖으로 밀려나는 종류의 사고다.
                                 그래서 자리는 그대로 두고 **누를 수 있게만** 만든다.

                              바로 갈 곳이 없는 슬라이드는 예전 그대로
                              글로만 알린다 — `route` 주석 참고.
                            */}
                            {slide.route ? (
                                <button
                                    type="button"
                                    onClick={handleGo}
                                    style={{
                                        width: 'fit-content',
                                        maxWidth: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 7,
                                        marginTop: 20,
                                        padding: '7px 13px 7px 11px',
                                        border: `1px solid ${slide.accent}55`,
                                        borderRadius: 9,
                                        color: slide.accent,
                                        backgroundColor: slide.softColor,
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                    }}
                                >
                                    <span style={{ width: 14, height: 1, flex: 'none', backgroundColor: slide.accent }} />
                                    {/* 「매출 정산 · 세션 현황 · 확인할 일」처럼 긴 자리 이름은
                                        두 줄로 접힌다. 단어 중간에서 끊기지 않게 keep-all 을 준다 */}
                                    <CaptionRegular
                                        style={{
                                            minWidth: 0,
                                            color: 'inherit',
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            wordBreak: 'keep-all',
                                        }}
                                    >
                                        {slide.menu} 보러가기
                                    </CaptionRegular>
                                    <ArrowRight size={14} style={{ flex: 'none' }} />
                                </button>
                            ) : (
                                <Row style={{ alignItems: 'center', gap: 7, marginTop: 24 }}>
                                    <span style={{ width: 18, height: 1, backgroundColor: slide.accent }} />
                                    <CaptionRegular style={{ color: slide.accent, fontWeight: 700 }}>
                                        {slide.menu}에서 확인하세요
                                    </CaptionRegular>
                                </Row>
                            )}
                        </Col>

                        <div
                            style={{
                                width: 392,
                                minHeight: 390,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: typePadding(slide.visual),
                                borderRadius: 26,
                                backgroundColor: `${slide.accent}0D`,
                                border: `1px solid ${slide.accent}18`,
                            }}
                        >
                            <FeatureVisual type={slide.visual} accent={slide.accent} />
                        </div>
                    </Row>
                </div>

                <Row style={{ alignItems: 'center', justifyContent: 'space-between', minHeight: 64, marginTop: 'auto' }}>
                    <button
                        type="button"
                        onClick={() => setChecked((prev) => !prev)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 190,
                            padding: 0,
                            border: 0,
                            background: 'transparent',
                            cursor: 'pointer',
                        }}
                    >
                        <CustomCheckbox className="mr-[8px]" checked={checked} />
                        <CaptionRegular style={{ color: '#667085' }}>이 업데이트 다시 보지 않기</CaptionRegular>
                    </button>

                    <Row style={{ alignItems: 'center', gap: 6 }}>
                        {UPDATE_SLIDES.map((item, index) => (
                            <button
                                key={item.category}
                                type="button"
                                aria-label={`${index + 1}번째 업데이트 보기`}
                                onClick={() => setCurrentIndex(index)}
                                style={{
                                    width: index === currentIndex ? 24 : 7,
                                    height: 7,
                                    padding: 0,
                                    border: 0,
                                    borderRadius: 999,
                                    backgroundColor: index === currentIndex ? slide.accent : '#D0D5DD',
                                    cursor: 'pointer',
                                    transition: 'width 0.18s ease, background-color 0.18s ease',
                                }}
                            />
                        ))}
                    </Row>

                    <Row style={{ minWidth: 190, justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                        {!isFirst && (
                            <button
                                type="button"
                                aria-label="이전 기능"
                                onClick={goPrevious}
                                style={{
                                    width: 42,
                                    height: 42,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: 0,
                                    border: '1px solid #D0D5DD',
                                    borderRadius: 10,
                                    color: '#475467',
                                    backgroundColor: '#FFFFFF',
                                    cursor: 'pointer',
                                }}
                            >
                                <ArrowLeft size={18} />
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={goNext}
                            style={{
                                width: 132,
                                height: 42,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 7,
                                padding: 0,
                                border: 0,
                                borderRadius: 10,
                                color: '#FFFFFF',
                                backgroundColor: slide.accent,
                                cursor: 'pointer',
                                fontSize: 14,
                                fontWeight: 700,
                            }}
                        >
                            {isLast ? '확인했어요' : '다음 기능'}
                            {!isLast && <ArrowRight size={16} />}
                        </button>
                    </Row>
                </Row>
            </Col>
        </Modal>
    );
};

const typePadding = (type: SlideVisual) => {
    if (type === 'app') return '20px 54px';
    return '30px 26px';
};

export default ModalUpdatePopupPublicMember;
